import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import Icon from './Icon';
import './Assets.css';
import {
  assetMatchesIntuneComplianceFilter,
  getIntuneComplianceFilterLabel,
  getAssetImei,
  getDeviceCompliancePolicyStates,
  formatDeviceCompliancePolicyTooltip
} from './utils/intuneCompliance';
import {
  NAMING_FIELD_TOKENS,
  NAMING_MATCH_MODES,
  NAMING_RULE_KINDS,
  assetPassesNamingFilter,
  describeNamingFilter,
  isNamingFilterActive,
  normalizeNamingMatchMode,
  normalizeNamingRuleKind
} from './utils/namingConvention';

const ASSET_TYPES = ['Computer', 'Mobile', 'Server', 'Network', 'Monitor', 'Peripheral', 'Docking Station', 'License'];
const ASSET_STATES = ['In Use', 'In Stock', 'In Transit', 'Retired'];
// Cost cadence for later annualized budgeting. Period was not stored separately:
// the requested period/frequency enums overlapped, so frequency is the single source of truth.
const ASSET_FREQUENCIES = ['One-time', 'Monthly', 'Quarterly', 'Semi-annually', 'Yearly', 'Custom'];
const COLUMN_PREFS_KEY = 'assets-visible-columns-v2';
const HIGHLIGHT_PREFS_KEY = 'assets-highlight-v1';
const IMEI_COLUMN = { field: 'imei', type: 'varchar' };
const DEFAULT_VISIBLE_COLUMNS = new Set([
  'serial',
  'name',
  'chassistype',
  'model',
  'assigned_to',
  'asset_state',
  'os-name'
]);

const isAutoSerialChassisType = (type) => {
  const normalized = String(type || '').trim().toLowerCase();
  return normalized === 'peripheral' || normalized === 'license';
};

const mapBillingCycleToFrequency = (cycle) => {
  const c = String(cycle || '').toLowerCase();
  if (!c) return '';
  if (c.includes('month')) return 'Monthly';
  if (c.includes('quarter')) return 'Quarterly';
  if (c.includes('semi')) return 'Semi-annually';
  if (c.includes('year') || c.includes('annual')) return 'Yearly';
  return '';
};

const formatCommitmentDateInput = (value) => {
  if (!value) return '';
  const text = String(value).trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const EMPTY_ADD_FORM = {
  serial: '',
  name: '',
  model: '',
  manufacturer: '',
  assigned_to: '',
  asset_state: '',
  type: '',
  price: '',
  frequency: '',
  warrantyEnd: '',
  licenseEntryId: ''
};

const formatAssetPrice = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getAssetColumnValue = (asset, columnField) => {
  const field = columnField.toLowerCase();
  if (field === 'chassistype') return asset.ChassisType || asset.type;
  if (field === 'imei') return getAssetImei(asset);
  return asset[columnField];
};

const getWarrantyDescription = (asset) => {
  if (!asset || typeof asset !== 'object') return '';
  const matchKey = Object.keys(asset).find((key) => {
    const normalized = String(key).toLowerCase().replace(/_/g, '-');
    return normalized === 'warranty-description';
  });
  if (!matchKey) return '';
  const value = asset[matchKey];
  return value != null && String(value).trim() !== '' ? String(value).trim() : '';
};

const Assets = ({ onAssetClick, initialFilters, intuneComplianceFilter: intuneComplianceFilterProp = null }) => {
  const { selectedTenant, authenticatedFetch } = useAuth();
  const [assets, setAssets] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchFilters, setSearchFilters] = useState(() => initialFilters || {});
  const [intuneComplianceFilter, setIntuneComplianceFilter] = useState(intuneComplianceFilterProp);
  const [showDarkDevices, setShowDarkDevices] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState(() => ({ ...EMPTY_ADD_FORM }));
  const [savingAssetStateId, setSavingAssetStateId] = useState(null);
  const [savingPriceId, setSavingPriceId] = useState(null);
  const [savingFrequencyId, setSavingFrequencyId] = useState(null);
  const [savingWarrantyEndId, setSavingWarrantyEndId] = useState(null);
  const [removingAssetId, setRemovingAssetId] = useState(null);
  const [noteModalAsset, setNoteModalAsset] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [noteError, setNoteError] = useState(null);
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [editPriceValue, setEditPriceValue] = useState('');
  const [editingWarrantyEndId, setEditingWarrantyEndId] = useState(null);
  const [editWarrantyEndValue, setEditWarrantyEndValue] = useState('');
  const [addError, setAddError] = useState(null);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addMode, setAddMode] = useState('single');
  const [jsonInput, setJsonInput] = useState('');
  const [importResults, setImportResults] = useState(null);
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [licenseSubscriptions, setLicenseSubscriptions] = useState([]);
  const [licenseSubscriptionsLoading, setLicenseSubscriptionsLoading] = useState(false);
  const [editingAssignedToId, setEditingAssignedToId] = useState(null);
  const [editAssignedToValue, setEditAssignedToValue] = useState('');
  const [editUserSuggestions, setEditUserSuggestions] = useState([]);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const columnPickerRef = useRef(null);
  const [showNamingFilterPicker, setShowNamingFilterPicker] = useState(false);
  const namingFilterPickerRef = useRef(null);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const stored = localStorage.getItem(COLUMN_PREFS_KEY);
      if (stored) return JSON.parse(stored);
    } catch {}
    return {};
  });
  const [highlightMode, setHighlightMode] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(HIGHLIGHT_PREFS_KEY) || '{}');
      return stored.mode === 'naming' ? 'naming' : 'none';
    } catch {
      return 'none';
    }
  });
  const [namingPattern, setNamingPattern] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(HIGHLIGHT_PREFS_KEY) || '{}');
      return typeof stored.pattern === 'string' ? stored.pattern : '';
    } catch {
      return '';
    }
  });
  const [namingMatchMode, setNamingMatchMode] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(HIGHLIGHT_PREFS_KEY) || '{}');
      return normalizeNamingMatchMode(stored.matchMode);
    } catch {
      return 'contains';
    }
  });
  const [namingRuleKind, setNamingRuleKind] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(HIGHLIGHT_PREFS_KEY) || '{}');
      return normalizeNamingRuleKind(stored.ruleKind);
    } catch {
      return 'mismatch';
    }
  });
  const namingPatternInputRef = useRef(null);

  const fetchAssets = useCallback(async () => {
    if (!selectedTenant) {
      setError('No tenant selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const includeDark = showDarkDevices ? '&includeDark=1' : '';
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/assets?tenant=${selectedTenant.name}${includeDark}`);

      const data = await response.json();

      if (response.ok) {
        setAssets(data.assets);
        setColumns(data.columns);
      } else {
        setError(data.error || 'Failed to fetch assets');
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, authenticatedFetch, showDarkDevices]);

  useEffect(() => {
    if (selectedTenant) {
      fetchAssets();
    }
  }, [selectedTenant, fetchAssets]);

  useEffect(() => {
    if (initialFilters && typeof initialFilters === 'object') {
      setSearchFilters(initialFilters);
    }
  }, [initialFilters]);

  useEffect(() => {
    setIntuneComplianceFilter(intuneComplianceFilterProp || null);
  }, [intuneComplianceFilterProp]);

  // Fetch user suggestions for typeahead (add modal)
  useEffect(() => {
    if (!selectedTenant || !userSearchQuery.trim()) {
      setUserSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await authenticatedFetch(
          `${getApiBaseUrl()}/api/tenant-users?tenant=${encodeURIComponent(selectedTenant.name)}&q=${encodeURIComponent(userSearchQuery.trim())}`
        );
        const data = await res.json();
        if (data.success && Array.isArray(data.users)) {
          setUserSuggestions(data.users);
        } else {
          setUserSuggestions([]);
        }
      } catch {
        setUserSuggestions([]);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [selectedTenant, userSearchQuery, authenticatedFetch]);

  // Fetch user suggestions for inline edit
  useEffect(() => {
    if (!selectedTenant || !editingAssignedToId || editAssignedToValue.length < 1) {
      setEditUserSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await authenticatedFetch(
          `${getApiBaseUrl()}/api/tenant-users?tenant=${encodeURIComponent(selectedTenant.name)}&q=${encodeURIComponent(editAssignedToValue.trim())}`
        );
        const data = await res.json();
        if (data.success && Array.isArray(data.users)) {
          setEditUserSuggestions(data.users);
        } else {
          setEditUserSuggestions([]);
        }
      } catch {
        setEditUserSuggestions([]);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [selectedTenant, editingAssignedToId, editAssignedToValue, authenticatedFetch]);

  // Close column picker when clicking outside
  useEffect(() => {
    if (!showColumnPicker) return;
    const handleClick = (e) => {
      if (columnPickerRef.current && !columnPickerRef.current.contains(e.target)) {
        setShowColumnPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showColumnPicker]);

  useEffect(() => {
    if (!showNamingFilterPicker) return;
    const handleClick = (e) => {
      if (namingFilterPickerRef.current && !namingFilterPickerRef.current.contains(e.target)) {
        setShowNamingFilterPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showNamingFilterPicker]);

  useEffect(() => {
    try {
      localStorage.setItem(HIGHLIGHT_PREFS_KEY, JSON.stringify({
        mode: highlightMode,
        pattern: namingPattern,
        matchMode: namingMatchMode,
        ruleKind: namingRuleKind
      }));
    } catch {}
  }, [highlightMode, namingPattern, namingMatchMode, namingRuleKind]);

  const formatIsoDate = (value) => {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      const dateOnlyMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateOnlyMatch) {
        return `${dateOnlyMatch[1]}-${dateOnlyMatch[2]}-${dateOnlyMatch[3]}`;
      }
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatValue = (value, column) => {
    if (value === null || value === undefined) {
      return <span className="null-value">-</span>;
    }

    const fieldName = String(column?.field || '').toLowerCase();
    if (fieldName === 'warranty-end' || fieldName === 'lastconnected') {
      const isoDate = formatIsoDate(value);
      return isoDate == null ? <span className="null-value">-</span> : isoDate;
    }
    if (fieldName === 'price') {
      const formatted = formatAssetPrice(value);
      return formatted == null ? <span className="null-value">-</span> : formatted;
    }
    
    // Format based on column type
    if (column.type && column.type.includes('int')) {
      return value.toString();
    }
    
    if (column.type && column.type.includes('date')) {
      return new Date(value).toLocaleDateString();
    }
    
    if (column.type && column.type.includes('datetime')) {
      return new Date(value).toLocaleString();
    }
    
    return value.toString();
  };

  const getColumnDisplayName = (fieldName) => {
    // Convert field names to display names
    const displayNames = {
      'id': 'ID',
      'name': 'Name',
      'serial': 'Serial Number',
      'os-name': 'Operating System',
      'model': 'Model',
      'manufacturer': 'Manufacturer',
      'status': 'Status',
      'location': 'Location',
      'assigned_to': 'Assigned To',
      'asset_state': 'Asset State',
      'price': 'Price',
      'frequency': 'Cost Frequency',
      'purchase_date': 'Purchase Date',
      'warranty_expiry': 'Warranty Expiry',
      'warranty-end': 'Warranty End',
      'ChassisType': 'Chassis Type',
      'imei': 'IMEI'
    };
    
    return displayNames[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Filter out ID column, type column, and unwanted columns, then sort columns
  const filteredColumns = columns.filter(column => {
    const fieldName = column.field.toLowerCase();
    return fieldName !== 'id' && 
           fieldName !== 'type' &&
           fieldName !== 'intune-raw' && 
           fieldName !== 'datto-raw' && 
           fieldName !== 'datto-uid' &&
           fieldName !== 'isactive' &&
           fieldName !== 'lastupdated' &&
           fieldName !== 'license_subscription_id' &&
           fieldName !== 'license_entry_id' &&
           fieldName !== 'note' &&
           (fieldName === 'warranty-end' || !fieldName.includes('warranty'));
  }).concat(
    columns.some((column) => column.field.toLowerCase() === 'imei') ? [] : [IMEI_COLUMN]
  ).sort((a, b) => {
    const order = ['serial', 'name', 'chassistype', 'model', 'assigned_to', 'asset_state', 'os-name', 'imei', 'manufacturer', 'price', 'frequency'];
    const aField = a.field.toLowerCase();
    const bField = b.field.toLowerCase();
    const aIdx = order.indexOf(aField);
    const bIdx = order.indexOf(bField);
    if (aIdx >= 0 && bIdx >= 0) return aIdx - bIdx;
    if (aIdx >= 0) return -1;
    if (bIdx >= 0) return 1;
    return 0;
  });

  const isColumnVisible = (fieldName, prefs = visibleColumns) => {
    const key = String(fieldName).toLowerCase();
    if (Object.prototype.hasOwnProperty.call(prefs, key)) {
      return prefs[key] !== false;
    }
    return DEFAULT_VISIBLE_COLUMNS.has(key);
  };

  // Columns to display (filtered by user visibility preference)
  const visibleColumnsList = filteredColumns.filter(col => isColumnVisible(col.field));
  const displayColumns = visibleColumnsList.length > 0 ? visibleColumnsList : filteredColumns;

  const toggleColumnVisibility = (fieldName) => {
    const key = fieldName.toLowerCase();
    const next = { ...visibleColumns, [key]: !isColumnVisible(key) };
    setVisibleColumns(next);
    try {
      localStorage.setItem(COLUMN_PREFS_KEY, JSON.stringify(next));
    } catch {}
  };

  const selectAllColumns = () => {
    const next = {};
    filteredColumns.forEach(col => { next[col.field.toLowerCase()] = true; });
    setVisibleColumns(next);
    try {
      localStorage.setItem(COLUMN_PREFS_KEY, JSON.stringify(next));
    } catch {}
  };

  const deselectAllColumns = () => {
    const next = {};
    filteredColumns.forEach(col => { next[col.field.toLowerCase()] = false; });
    setVisibleColumns(next);
    try {
      localStorage.setItem(COLUMN_PREFS_KEY, JSON.stringify(next));
    } catch {}
  };

  // Handle search filter changes
  const handleSearchChange = (columnField, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [columnField]: value.toLowerCase()
    }));
  };

  // Clear all search filters
  const clearAllFilters = () => {
    setSearchFilters({});
    setIntuneComplianceFilter(null);
    setHighlightMode('none');
  };

  const namingFilterActive = isNamingFilterActive(highlightMode, namingPattern);
  const hasActiveFilters = Object.keys(searchFilters).length > 0
    || Boolean(intuneComplianceFilter)
    || namingFilterActive;

  // Filter and sort assets based on search filters and sort configuration
  const filteredAndSortedAssets = [...assets]
    .filter(item => {
      if (intuneComplianceFilter) {
        if (!assetMatchesIntuneComplianceFilter(item, intuneComplianceFilter, 'nonCompliant')) {
          return false;
        }
      }

      if (isNamingFilterActive(highlightMode, namingPattern)) {
        if (!assetPassesNamingFilter(item, namingPattern, namingMatchMode, namingRuleKind)) {
          return false;
        }
      }

      // Apply search filters
      return Object.entries(searchFilters).every(([columnField, searchValue]) => {
        if (!searchValue) return true; // No filter applied for this column
        
        const itemValue = getAssetColumnValue(item, columnField);
        if (itemValue === null || itemValue === undefined) return false;
        
        return String(itemValue).toLowerCase().includes(searchValue);
      });
    })
    .sort((a, b) => {
      if (!sortConfig.key) return 0;
      
      const getVal = (x) => getAssetColumnValue(x, sortConfig.key);
      const aValue = getVal(a);
      const bValue = getVal(b);
      
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // Convert to string for comparison
      const aStr = String(aValue);
      const bStr = String(bValue);
      return sortConfig.direction === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });

  const selectHighlightMode = (mode) => {
    setHighlightMode(mode);
  };

  const insertNamingToken = (tokenId) => {
    const tokenText = `{${tokenId}}`;
    const input = namingPatternInputRef.current;
    if (!input) {
      setNamingPattern((prev) => `${prev}${tokenText}`);
      return;
    }
    const start = input.selectionStart ?? namingPattern.length;
    const end = input.selectionEnd ?? namingPattern.length;
    const next = `${namingPattern.slice(0, start)}${tokenText}${namingPattern.slice(end)}`;
    setNamingPattern(next);
    requestAnimationFrame(() => {
      input.focus();
      const cursor = start + tokenText.length;
      input.setSelectionRange(cursor, cursor);
    });
  };

  // Handle column sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const fetchLicenseSubscriptions = useCallback(async () => {
    if (!selectedTenant?.name) {
      setLicenseSubscriptions([]);
      return;
    }
    setLicenseSubscriptionsLoading(true);
    try {
      const res = await authenticatedFetch(
        `${getApiBaseUrl()}/api/cloudfactory/active?tenant=${encodeURIComponent(selectedTenant.name)}`
      );
      const data = await res.json();
      if (!res.ok) {
        console.error('Failed to load active subscriptions:', data.error);
        setLicenseSubscriptions([]);
        return;
      }
      setLicenseSubscriptions(Array.isArray(data.subscriptions) ? data.subscriptions : []);
    } catch (err) {
      console.error('Failed to load active subscriptions:', err);
      setLicenseSubscriptions([]);
    } finally {
      setLicenseSubscriptionsLoading(false);
    }
  }, [selectedTenant, authenticatedFetch]);

  const openAddModal = () => {
    setShowAddModal(true);
    setAddForm({ ...EMPTY_ADD_FORM });
    setAddError(null);
    setAddMode('single');
    setJsonInput('');
    setImportResults(null);
    setUserSearchQuery('');
    setUserSuggestions([]);
    fetchLicenseSubscriptions();
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAddError(null);
    setAddSubmitting(false);
  };

  const applyLicenseSubscription = (entryId) => {
    const selected = licenseSubscriptions.find((item) => String(item.entryId) === String(entryId));
    if (!selected) {
      setAddForm((f) => ({ ...f, licenseEntryId: '', name: '', model: '' }));
      return;
    }
    const mappedFrequency = mapBillingCycleToFrequency(selected.billingCycle);
    const commitmentEnd = formatCommitmentDateInput(selected.commitmentEndDate || selected.endDate);
    setAddForm((f) => ({
      ...f,
      licenseEntryId: String(selected.entryId),
      name: selected.name || f.name,
      model: selected.sku || f.model,
      manufacturer: f.manufacturer || 'CloudFactory',
      frequency: mappedFrequency || f.frequency,
      warrantyEnd: commitmentEnd || f.warrantyEnd
    }));
  };

  const handleAddAsset = async (e) => {
    e.preventDefault();
    setAddError(null);
    const {
      serial,
      name,
      model,
      manufacturer,
      assigned_to,
      asset_state,
      type,
      price,
      frequency,
      warrantyEnd,
      licenseEntryId
    } = addForm;
    if (!type) {
      setAddError('Please select a chassis type');
      return;
    }
    if (type === 'License' && !licenseEntryId) {
      setAddError('Please select an Active Subscription for this license');
      return;
    }
    if (!isAutoSerialChassisType(type) && !serial.trim()) {
      setAddError('Serial number is required');
      return;
    }
    if (price !== '' && price != null) {
      const n = Number(String(price).replace(',', '.'));
      if (!Number.isFinite(n)) {
        setAddError('Price must be a number');
        return;
      }
      if (n < 0) {
        setAddError('Price cannot be negative');
        return;
      }
    }
    setAddSubmitting(true);
    try {
      const res = await authenticatedFetch(
        `${getApiBaseUrl()}/api/assets?tenant=${encodeURIComponent(selectedTenant.name)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            serial: serial.trim() || undefined,
            name: name.trim() || undefined,
            model: model.trim() || undefined,
            manufacturer: manufacturer.trim() || undefined,
            assigned_to: assigned_to.trim() || undefined,
            asset_state: asset_state || undefined,
            chassisType: type || undefined,
            price: price === '' || price == null ? null : Number(String(price).replace(',', '.')),
            frequency: frequency || undefined,
            warrantyEnd: warrantyEnd || undefined,
            licenseEntryId: type === 'License' ? licenseEntryId : undefined
          })
        }
      );
      const data = await res.json();
      if (!res.ok) {
        setAddError(data.error || 'Failed to add asset');
        return;
      }
      closeAddModal();
      fetchAssets();
    } catch (err) {
      setAddError('Network error. Please try again.');
    } finally {
      setAddSubmitting(false);
    }
  };

  const parseAndMapJsonDevice = (item) => {
    const manufacturer = (item.SystemManufacturer || '').trim() || null;
    const chassisName = (item.ChassisName || '').trim();
    const type = ASSET_TYPES.includes(chassisName) ? chassisName : 'Peripheral';
    const model = (item.MachineType || '').trim() || null;
    const serial = (item.SerialNumber || item.SerialNumberLong || '').trim() || null;
    const lastConnected = (item.Discovered || '').trim() || null;
    return { manufacturer, type, model, serial, lastConnected };
  };

  const handleImportFromJson = async (e) => {
    e.preventDefault();
    setAddError(null);
    setImportResults(null);

    let parsed;
    try {
      parsed = typeof jsonInput === 'string' ? JSON.parse(jsonInput) : jsonInput;
    } catch {
      setAddError('Invalid JSON. Please check the format.');
      return;
    }

    const peripherals = Array.isArray(parsed?.Peripheral) ? parsed.Peripheral : [];
    if (peripherals.length === 0) {
      setAddError('No "Peripheral" array found in JSON, or it is empty.');
      return;
    }

    const upn = (parsed.UPN || '').trim() || null;

    setAddSubmitting(true);
    const results = { success: [], failed: [] };

    for (const item of peripherals) {
      const { manufacturer, type, model, serial, lastConnected } = parseAndMapJsonDevice(item);
      if (!serial && type !== 'Peripheral') {
        results.failed.push({ item, error: 'Serial number is required' });
        continue;
      }

      try {
        const res = await authenticatedFetch(
          `${getApiBaseUrl()}/api/assets?tenant=${encodeURIComponent(selectedTenant.name)}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              serial: serial || undefined,
              model: model || undefined,
              manufacturer: manufacturer || undefined,
              chassisType: type || undefined,
              upn: upn || undefined,
              LastConnected: lastConnected || undefined
            })
          }
        );
        const data = await res.json();
        if (res.ok) {
          results.success.push({ serial: serial || data.serial, model });
        } else {
          results.failed.push({ item, error: data.error || 'Failed to add' });
        }
      } catch (err) {
        results.failed.push({ item, error: 'Network error' });
      }
    }

    setImportResults(results);
    setAddSubmitting(false);
    if (results.success.length > 0) {
      fetchAssets();
    }
  };

  const handleJsonFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target?.result;
        if (typeof text === 'string') setJsonInput(text);
      } catch {}
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const saveAssignedTo = async (assetId, valueOverride) => {
    if (!assetId || !selectedTenant) return;
    const value = (valueOverride !== undefined ? valueOverride : editAssignedToValue || '').trim();
    try {
      const res = await authenticatedFetch(
        `${getApiBaseUrl()}/api/assets/${assetId}/assigned-to?tenant=${encodeURIComponent(selectedTenant.name)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assigned_to: value || null })
        }
      );
      const data = await res.json();
      if (!res.ok) {
        console.error('Failed to update assigned to:', data.error);
        return;
      }
      setAssets(prev => prev.map(a => a.id === assetId ? { ...a, assigned_to: value || null } : a));
    } catch (err) {
      console.error('Failed to update assigned to:', err);
    } finally {
      setEditingAssignedToId(null);
      setEditAssignedToValue('');
      setEditUserSuggestions([]);
    }
  };

  const startEditAssignedTo = (asset) => {
    setEditingAssignedToId(asset.id);
    setEditAssignedToValue(asset.assigned_to || '');
    setEditUserSuggestions([]);
  };

  const saveAssetState = async (assetId, value) => {
    if (!assetId || !selectedTenant) return;
    const normalized = value || null;
    setSavingAssetStateId(assetId);
    try {
      const res = await authenticatedFetch(
        `${getApiBaseUrl()}/api/assets/${assetId}/asset-state?tenant=${encodeURIComponent(selectedTenant.name)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ asset_state: normalized })
        }
      );
      const data = await res.json();
      if (!res.ok) {
        console.error('Failed to update asset state:', data.error);
        return;
      }
      setAssets(prev => prev.map(a => a.id === assetId ? { ...a, asset_state: data.asset_state ?? normalized } : a));
    } catch (err) {
      console.error('Failed to update asset state:', err);
    } finally {
      setSavingAssetStateId(null);
    }
  };

  const saveAssetPrice = async (assetId, value) => {
    if (!assetId || !selectedTenant) return;
    const trimmed = value == null ? '' : String(value).trim();
    let payload = null;
    if (trimmed !== '') {
      const n = Number(trimmed.replace(',', '.'));
      if (!Number.isFinite(n) || n < 0) {
        setEditingPriceId(null);
        setEditPriceValue('');
        return;
      }
      payload = n;
    }
    setSavingPriceId(assetId);
    try {
      const res = await authenticatedFetch(
        `${getApiBaseUrl()}/api/assets/${assetId}/price?tenant=${encodeURIComponent(selectedTenant.name)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ price: payload })
        }
      );
      const data = await res.json();
      if (!res.ok) {
        console.error('Failed to update price:', data.error);
        return;
      }
      setAssets((prev) => prev.map((a) => (a.id === assetId ? { ...a, price: data.price } : a)));
    } catch (err) {
      console.error('Failed to update price:', err);
    } finally {
      setSavingPriceId(null);
      setEditingPriceId(null);
      setEditPriceValue('');
    }
  };

  const saveAssetFrequency = async (assetId, value) => {
    if (!assetId || !selectedTenant) return;
    const normalized = value || null;
    setSavingFrequencyId(assetId);
    try {
      const res = await authenticatedFetch(
        `${getApiBaseUrl()}/api/assets/${assetId}/frequency?tenant=${encodeURIComponent(selectedTenant.name)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ frequency: normalized })
        }
      );
      const data = await res.json();
      if (!res.ok) {
        console.error('Failed to update frequency:', data.error);
        return;
      }
      setAssets((prev) => prev.map((a) => (a.id === assetId ? { ...a, frequency: data.frequency ?? normalized } : a)));
    } catch (err) {
      console.error('Failed to update frequency:', err);
    } finally {
      setSavingFrequencyId(null);
    }
  };

  const saveAssetWarrantyEnd = async (assetId, value) => {
    if (!assetId || !selectedTenant) return;
    const isoDate = formatIsoDate(value);
    const payload = isoDate && /^\d{4}-\d{2}-\d{2}$/.test(isoDate) ? isoDate : null;
    setSavingWarrantyEndId(assetId);
    try {
      const res = await authenticatedFetch(
        `${getApiBaseUrl()}/api/assets/${assetId}/warranty-end?tenant=${encodeURIComponent(selectedTenant.name)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ warrantyEnd: payload })
        }
      );
      const data = await res.json();
      if (!res.ok) {
        console.error('Failed to update warranty end:', data.error);
        return;
      }
      const nextValue = data['warranty-end'] ?? payload;
      setAssets((prev) => prev.map((a) => (a.id === assetId ? { ...a, 'warranty-end': nextValue } : a)));
    } catch (err) {
      console.error('Failed to update warranty end:', err);
    } finally {
      setSavingWarrantyEndId(null);
      setEditingWarrantyEndId(null);
      setEditWarrantyEndValue('');
    }
  };

  const removeAsset = async (asset) => {
    if (!asset?.id || !selectedTenant) return;
    const label = asset.serial || asset.name || `asset #${asset.id}`;
    if (!window.confirm(`Remove ${label}?\n\nThis sets the asset inactive (isActive = 0). It will appear under Show Dark Devices.`)) {
      return;
    }
    setRemovingAssetId(asset.id);
    try {
      const res = await authenticatedFetch(
        `${getApiBaseUrl()}/api/assets/${asset.id}/deactivate?tenant=${encodeURIComponent(selectedTenant.name)}`,
        { method: 'PATCH' }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.error('Failed to remove asset:', data.error);
        window.alert(data.error || 'Failed to remove asset');
        return;
      }
      if (showDarkDevices) {
        setAssets((prev) => prev.map((a) => (
          a.id === asset.id ? { ...a, isActive: 0, isactive: 0 } : a
        )));
      } else {
        setAssets((prev) => prev.filter((a) => a.id !== asset.id));
      }
    } catch (err) {
      console.error('Failed to remove asset:', err);
      window.alert('Network error. Please try again.');
    } finally {
      setRemovingAssetId(null);
    }
  };

  const openNoteModal = (asset) => {
    setNoteModalAsset(asset);
    setNoteDraft(asset?.note != null ? String(asset.note) : '');
    setNoteError(null);
  };

  const closeNoteModal = () => {
    if (savingNote) return;
    setNoteModalAsset(null);
    setNoteDraft('');
    setNoteError(null);
  };

  const saveAssetNote = async () => {
    if (!noteModalAsset?.id || !selectedTenant) return;
    setSavingNote(true);
    setNoteError(null);
    try {
      const res = await authenticatedFetch(
        `${getApiBaseUrl()}/api/assets/${noteModalAsset.id}/note?tenant=${encodeURIComponent(selectedTenant.name)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: noteDraft })
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setNoteError(data.error || 'Failed to save note');
        return;
      }
      const nextNote = data.note ?? (String(noteDraft).trim() || null);
      setAssets((prev) => prev.map((a) => (
        a.id === noteModalAsset.id ? { ...a, note: nextNote } : a
      )));
      setNoteModalAsset(null);
      setNoteDraft('');
    } catch (err) {
      console.error('Failed to save note:', err);
      setNoteError('Network error. Please try again.');
    } finally {
      setSavingNote(false);
    }
  };

  const getAssetNote = (asset) => {
    if (!asset) return '';
    const value = asset.note;
    return value != null && String(value).trim() !== '' ? String(value).trim() : '';
  };

  if (loading) {
    return (
      <div className="assets-container">
        <div className="assets-header">
          <h2>Assets overview</h2>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading assets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="assets-container">
        <div className="assets-header">
          <h2>Assets overview</h2>
        </div>
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h3>Error Loading Assets</h3>
          <p>{error}</p>
          <button onClick={fetchAssets} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!selectedTenant) {
    return (
      <div className="assets-container">
        <div className="assets-header">
          <h2>Assets</h2>
        </div>
        <div className="no-tenant-container">
          <div className="no-tenant-icon">🏢</div>
          <h3>No Tenant Selected</h3>
          <p>Please select a tenant from the dropdown to view assets.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="assets-container">
      <div className="assets-header">
        <div className="assets-header-left">
          <h2>Assets overview</h2>
          {intuneComplianceFilter && (
            <div className="assets-intune-filter-banner">
              <span>{getIntuneComplianceFilterLabel(intuneComplianceFilter)}</span>
              <button type="button" onClick={() => setIntuneComplianceFilter(null)}>
                Remove filter
              </button>
            </div>
          )}
          {namingFilterActive && (
            <div className="assets-intune-filter-banner">
              <span>
                {describeNamingFilter(namingPattern, namingMatchMode, namingRuleKind)}
                {` · ${filteredAndSortedAssets.length} shown`}
              </span>
              <button type="button" onClick={() => setHighlightMode('none')}>
                Remove filter
              </button>
            </div>
          )}
        </div>
        <div className="assets-actions">
          <button onClick={openAddModal} className="add-asset-button">
            <Icon name="add" size="small" />
            Add Asset
          </button>
          <button onClick={fetchAssets} className="refresh-button">
            <Icon name="refresh" size="small" />
            Refresh
          </button>
          {hasActiveFilters && (
            <button onClick={clearAllFilters} className="clear-filters-button">
              Clear Filters
            </button>
          )}
          <label className="dark-devices-toggle">
            <input
              type="checkbox"
              checked={showDarkDevices}
              onChange={(e) => setShowDarkDevices(e.target.checked)}
            />
            <span>Show Dark Devices</span>
          </label>
          <span className="assets-count">
            {filteredAndSortedAssets.length} of {assets.length} asset{assets.length !== 1 ? 's' : ''}
          </span>
          <div className="column-picker-wrapper" ref={namingFilterPickerRef}>
            <button
              type="button"
              className={`column-picker-button ${namingFilterActive ? 'is-active' : ''}`}
              onClick={() => {
                setShowColumnPicker(false);
                setShowNamingFilterPicker(!showNamingFilterPicker);
              }}
              title="Naming filter"
              aria-expanded={showNamingFilterPicker}
            >
              <Icon name="flag" size="small" />
            </button>
            {showNamingFilterPicker && (
              <div className="column-picker-dropdown highlight-picker-dropdown">
                <div className="column-picker-header">Naming filter</div>
                <div className="column-picker-list">
                  <label className="column-picker-item">
                    <input
                      type="radio"
                      name="asset-highlight-mode"
                      checked={highlightMode === 'none'}
                      onChange={() => selectHighlightMode('none')}
                    />
                    <span>Off</span>
                  </label>
                  <label className="column-picker-item">
                    <input
                      type="radio"
                      name="asset-highlight-mode"
                      checked={highlightMode === 'naming'}
                      onChange={() => selectHighlightMode('naming')}
                    />
                    <span>Naming convention</span>
                  </label>
                </div>
                {highlightMode === 'naming' && (
                  <div className="naming-convention-filter">
                    <div className="naming-rule-row">
                      <span>Name</span>
                      <select
                        id="naming-convention-kind"
                        value={namingRuleKind}
                        onChange={(e) => setNamingRuleKind(normalizeNamingRuleKind(e.target.value))}
                        aria-label="Naming convention rule kind"
                      >
                        {NAMING_RULE_KINDS.map((kind) => (
                          <option key={kind.id} value={kind.id}>{kind.label}</option>
                        ))}
                      </select>
                      <select
                        id="naming-convention-mode"
                        value={namingMatchMode}
                        onChange={(e) => setNamingMatchMode(normalizeNamingMatchMode(e.target.value))}
                        aria-label="Naming convention match mode"
                      >
                        {NAMING_MATCH_MODES.map((mode) => (
                          <option key={mode.id} value={mode.id}>{mode.label}</option>
                        ))}
                      </select>
                    </div>
                    <input
                      ref={namingPatternInputRef}
                      type="text"
                      value={namingPattern}
                      onChange={(e) => setNamingPattern(e.target.value)}
                      placeholder="e.g. PC-{serialnumber}"
                      className="naming-convention-custom-input"
                      aria-label="Naming convention pattern"
                    />
                    <div className="naming-token-row">
                      <span>Insert</span>
                      <div className="naming-token-list">
                        {NAMING_FIELD_TOKENS.map((token) => (
                          <button
                            key={token.id}
                            type="button"
                            className="naming-token-button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => insertNamingToken(token.id)}
                          >
                            {token.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <p className="naming-convention-hint">
                      {namingRuleKind === 'match'
                        ? 'Only assets whose name matches this rule are shown.'
                        : 'Only assets whose name does not match this rule are shown.'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="column-picker-wrapper" ref={columnPickerRef}>
            <button
              type="button"
              className="column-picker-button"
              onClick={() => {
                setShowNamingFilterPicker(false);
                setShowColumnPicker(!showColumnPicker);
              }}
              title="Select columns"
            >
              <Icon name="settings" size="small" />
            </button>
            {showColumnPicker && (
              <div className="column-picker-dropdown">
                <div className="column-picker-header">Columns</div>
                <div className="column-picker-actions">
                  <button type="button" onClick={selectAllColumns}>Show all</button>
                  <button type="button" onClick={deselectAllColumns}>Hide all</button>
                </div>
                <div className="column-picker-list">
                  {filteredColumns.map((column) => {
                    const key = column.field.toLowerCase();
                    const isVisible = isColumnVisible(key);
                    return (
                      <label key={column.field} className="column-picker-item">
                        <input
                          type="checkbox"
                          checked={isVisible}
                          onChange={() => toggleColumnVisibility(column.field)}
                        />
                        <span>{getColumnDisplayName(column.field)}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {assets.length === 0 ? (
        <div className="no-assets-container">
          <Icon name="assets" size="large" className="no-assets-icon" />
          <h3>No Assets Found</h3>
          <p>No assets found.</p>
        </div>
      ) : (
        <div className="assets-grid-container">
          <div className="assets-grid">
            <table className="assets-table">
              <thead>
                <tr>
                  {displayColumns.map((column) => (
                    <th 
                      key={column.field} 
                      className="table-header sortable"
                      onClick={() => handleSort(column.field)}
                    >
                      <div className="header-content">
                        <span>{getColumnDisplayName(column.field)}</span>
                        {sortConfig.key === column.field && (
                          <span className="sort-indicator">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="table-header asset-actions-header">Actions</th>
                </tr>
                <tr className="search-row">
                  {visibleColumnsList.map((column) => (
                    <th key={`search-${column.field}`} className="search-header">
                      <input
                        type="text"
                        placeholder={`Search ${getColumnDisplayName(column.field)}...`}
                        value={searchFilters[column.field] || ''}
                        onChange={(e) => handleSearchChange(column.field, e.target.value)}
                        className="column-search-input"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                  ))}
                  <th className="search-header asset-actions-header" />
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedAssets.map((asset, index) => {
                  const isDarkDevice = asset.isActive === 0 || asset.isActive === false || asset.isactive === 0 || asset.isactive === false;
                  const compliancePolicies = getDeviceCompliancePolicyStates(asset);
                  return (
                  <tr 
                    key={asset.id || index} 
                    className={`table-row ${isDarkDevice ? 'dark-device-row' : ''}`}
                  >
                    {displayColumns.map((column) => {
                      const isSerialColumn = column.field.toLowerCase() === 'serial';
                      const isAssignedToColumn = column.field.toLowerCase() === 'assigned_to';
                      const isAssetStateColumn = column.field.toLowerCase() === 'asset_state';
                      const isPriceColumn = column.field.toLowerCase() === 'price';
                      const isFrequencyColumn = column.field.toLowerCase() === 'frequency';
                      const isWarrantyEndColumn = column.field.toLowerCase() === 'warranty-end';
                      const cellValue = getAssetColumnValue(asset, column.field);
                      const hasValue = cellValue && cellValue !== '';
                      const isEditingThis = isAssignedToColumn && editingAssignedToId === asset.id;
                      const isEditingPrice = isPriceColumn && editingPriceId === asset.id;
                      const isEditingWarrantyEnd = isWarrantyEndColumn && editingWarrantyEndId === asset.id;
                      const warrantyDescription = isWarrantyEndColumn && !isEditingWarrantyEnd ? getWarrantyDescription(asset) : '';
                      const isEditableEmptyCell = (isPriceColumn || isWarrantyEndColumn) && !hasValue;

                      const handleCellClick = () => {
                        if (isEditingThis || isAssetStateColumn || isFrequencyColumn || isEditingPrice || isEditingWarrantyEnd) return;
                        if (isAssignedToColumn) {
                          startEditAssignedTo(asset);
                          return;
                        }
                        if (isPriceColumn) {
                          setEditingPriceId(asset.id);
                          setEditPriceValue(asset.price != null && asset.price !== '' ? String(asset.price) : '');
                          return;
                        }
                        if (isWarrantyEndColumn) {
                          const isoDate = formatIsoDate(cellValue);
                          setEditingWarrantyEndId(asset.id);
                          setEditWarrantyEndValue(isoDate && /^\d{4}-\d{2}-\d{2}$/.test(isoDate) ? isoDate : '');
                          return;
                        }
                        if (isSerialColumn && asset.serial) {
                          onAssetClick && onAssetClick(asset.serial);
                        } else if (!isSerialColumn && hasValue) {
                          handleSearchChange(column.field, cellValue);
                        }
                      };

                      if (isAssetStateColumn) {
                        const currentState = asset.asset_state || '';
                        return (
                          <td key={column.field} className="table-cell asset-state-cell" onClick={(e) => e.stopPropagation()}>
                            <select
                              className="asset-state-select"
                              value={currentState}
                              disabled={savingAssetStateId === asset.id}
                              onChange={(e) => saveAssetState(asset.id, e.target.value || null)}
                            >
                              <option value="">—</option>
                              {ASSET_STATES.map((state) => (
                                <option key={state} value={state}>{state}</option>
                              ))}
                            </select>
                          </td>
                        );
                      }

                      if (isFrequencyColumn) {
                        const currentFrequency = asset.frequency || '';
                        return (
                          <td key={column.field} className="table-cell asset-state-cell" onClick={(e) => e.stopPropagation()}>
                            <select
                              className="asset-state-select"
                              value={currentFrequency}
                              disabled={savingFrequencyId === asset.id}
                              onChange={(e) => saveAssetFrequency(asset.id, e.target.value || null)}
                              title="How often this cost is incurred"
                            >
                              <option value="">—</option>
                              {ASSET_FREQUENCIES.map((freq) => (
                                <option key={freq} value={freq}>{freq}</option>
                              ))}
                            </select>
                          </td>
                        );
                      }

                      if (isPriceColumn && isEditingPrice) {
                        return (
                          <td key={column.field} className="table-cell asset-state-cell" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="asset-price-input"
                              value={editPriceValue}
                              disabled={savingPriceId === asset.id}
                              onChange={(e) => setEditPriceValue(e.target.value)}
                              onBlur={() => saveAssetPrice(asset.id, editPriceValue)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveAssetPrice(asset.id, editPriceValue);
                                if (e.key === 'Escape') {
                                  setEditingPriceId(null);
                                  setEditPriceValue('');
                                }
                              }}
                              autoFocus
                              placeholder="0.00"
                            />
                          </td>
                        );
                      }

                      if (isWarrantyEndColumn && isEditingWarrantyEnd) {
                        return (
                          <td key={column.field} className="table-cell asset-state-cell" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="date"
                              className="asset-price-input asset-date-input"
                              value={editWarrantyEndValue}
                              disabled={savingWarrantyEndId === asset.id}
                              onChange={(e) => setEditWarrantyEndValue(e.target.value)}
                              onBlur={() => saveAssetWarrantyEnd(asset.id, editWarrantyEndValue)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveAssetWarrantyEnd(asset.id, editWarrantyEndValue);
                                if (e.key === 'Escape') {
                                  setEditingWarrantyEndId(null);
                                  setEditWarrantyEndValue('');
                                }
                              }}
                              autoFocus
                            />
                          </td>
                        );
                      }

                      if (isAssignedToColumn && isEditingThis) {
                        return (
                          <td key={column.field} className="table-cell assigned-to-edit-cell" onClick={(e) => e.stopPropagation()}>
                            <div className="assigned-to-typeahead">
                              <input
                                type="text"
                                className="assigned-to-input"
                                value={editAssignedToValue}
                                onChange={(e) => setEditAssignedToValue(e.target.value)}
                                onBlur={() => setTimeout(() => saveAssignedTo(asset.id), 150)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveAssignedTo(asset.id);
                                  if (e.key === 'Escape') {
                                    setEditingAssignedToId(null);
                                    setEditAssignedToValue('');
                                  }
                                }}
                                autoFocus
                                placeholder="Search user..."
                              />
                              {editUserSuggestions.length > 0 && (
                                <ul className="typeahead-dropdown">
                                  {editUserSuggestions.map((u, i) => {
                                    const displayName = u.displayName || u.displayname || u.userPrincipalName || u.userprincipalname || '';
                                    return (
                                    <li
                                      key={i}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        setEditAssignedToValue(displayName);
                                        setEditUserSuggestions([]);
                                        saveAssignedTo(asset.id, displayName);
                                      }}
                                    >
                                      {displayName}
                                    </li>
                                  );
                                  })}
                                </ul>
                              )}
                            </div>
                          </td>
                        );
                      }

                      return (
                        <td 
                          key={column.field} 
                          className={`table-cell ${isAssignedToColumn ? 'assigned-to-cell' : ''} ${(isSerialColumn && asset.serial) || (!isSerialColumn && hasValue) || isEditableEmptyCell ? 'clickable-cell' : ''} ${warrantyDescription ? 'has-warranty-tooltip' : ''}`}
                          onClick={handleCellClick}
                          title={warrantyDescription || (isWarrantyEndColumn && !hasValue ? 'Click to set warranty end' : undefined)}
                          style={{ 
                            cursor: warrantyDescription
                              ? 'help'
                              : isWarrantyEndColumn || isPriceColumn || (isSerialColumn && asset.serial) || !isSerialColumn ? 'pointer' : 'default' 
                          }}
                        >
                          {isAssignedToColumn && (
                            <span className="edit-assigned-icon" title="Edit assigned to">
                              <Icon name="edit" size="small" />
                            </span>
                          )}
                          {formatValue(cellValue, column)}
                        </td>
                      );
                    })}
                    <td className="table-cell asset-actions-cell" onClick={(e) => e.stopPropagation()}>
                      <div className="asset-actions-row">
                        {compliancePolicies.length > 0 && (
                          <span
                            className="asset-action-button asset-compliance-lock"
                            title={formatDeviceCompliancePolicyTooltip(compliancePolicies)}
                            aria-label="Device compliance policies"
                          >
                            <Icon name="lock" size="small" className="asset-action-icon" />
                          </span>
                        )}
                        <button
                          type="button"
                          className={`asset-action-button asset-note-button ${getAssetNote(asset) ? 'has-note' : ''}`}
                          title={getAssetNote(asset) ? getAssetNote(asset) : 'Add note'}
                          aria-label={getAssetNote(asset) ? 'Edit note' : 'Add note'}
                          onClick={() => openNoteModal(asset)}
                        >
                          <Icon name="info" size="small" className="asset-action-icon" />
                        </button>
                        {!isDarkDevice && (
                          <button
                            type="button"
                            className="asset-action-button asset-remove-button"
                            title="Remove asset (set inactive)"
                            aria-label="Remove asset"
                            disabled={removingAssetId === asset.id}
                            onClick={() => removeAsset(asset)}
                          >
                            <Icon name="trash" size="small" className="asset-action-icon" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="asset-modal-overlay" onClick={closeAddModal}>
          <div className="asset-modal asset-modal-wide" onClick={(e) => e.stopPropagation()}>
            <h3>Add Asset</h3>
            <div className="asset-modal-tabs">
              <button
                type="button"
                className={`asset-modal-tab ${addMode === 'single' ? 'active' : ''}`}
                onClick={() => { setAddMode('single'); setAddError(null); setImportResults(null); }}
              >
                Add single
              </button>
              <button
                type="button"
                className={`asset-modal-tab ${addMode === 'import' ? 'active' : ''}`}
                onClick={() => { setAddMode('import'); setAddError(null); setImportResults(null); }}
              >
                Import from JSON
              </button>
            </div>

            {addMode === 'single' ? (
            <form onSubmit={handleAddAsset}>
              {addError && <div className="asset-modal-error">{addError}</div>}
              <div className="asset-modal-row">
                <label>Chassis Type *</label>
                <select
                  value={addForm.type}
                  onChange={(e) => {
                    const t = e.target.value;
                    setAddForm(f => ({
                      ...f,
                      type: t,
                      serial: isAutoSerialChassisType(t) ? '' : f.serial,
                      licenseEntryId: t === 'License' ? f.licenseEntryId : '',
                      name: t === 'License' ? f.name : f.name,
                      manufacturer: t === 'License' && !f.manufacturer ? 'CloudFactory' : f.manufacturer
                    }));
                    if (t === 'License') fetchLicenseSubscriptions();
                  }}
                  required
                >
                  <option value="">-- Select chassis type --</option>
                  {ASSET_TYPES.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              {addForm.type === 'License' && (
                <div className="asset-modal-row">
                  <label>Active Subscription *</label>
                  <select
                    value={addForm.licenseEntryId}
                    onChange={(e) => applyLicenseSubscription(e.target.value)}
                    required
                    disabled={licenseSubscriptionsLoading}
                  >
                    <option value="">
                      {licenseSubscriptionsLoading ? 'Loading subscriptions...' : '-- Select subscription --'}
                    </option>
                    {licenseSubscriptions.map((sub) => (
                      <option key={sub.entryId} value={sub.entryId}>
                        {sub.name}
                        {sub.quantity != null ? ` (qty ${sub.quantity})` : ''}
                        {sub.billingCycle ? ` · ${sub.billingCycle}` : ''}
                      </option>
                    ))}
                  </select>
                  {addForm.licenseEntryId && (() => {
                    const selected = licenseSubscriptions.find((item) => String(item.entryId) === String(addForm.licenseEntryId));
                    const qty = selected?.quantity != null && Number(selected.quantity) > 0 ? Number(selected.quantity) : 1;
                    const unit = addForm.price !== '' && addForm.price != null ? Number(String(addForm.price).replace(',', '.')) : null;
                    const lineTotal = unit != null && Number.isFinite(unit) ? unit * qty : null;
                    return (
                      <div className="asset-license-hint">
                        Quantity from SKU: {qty}
                        {lineTotal != null ? ` · Budgeted line total: ${lineTotal.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}
                      </div>
                    );
                  })()}
                  {!licenseSubscriptionsLoading && licenseSubscriptions.length === 0 && (
                    <div className="asset-license-hint">No Active Subscriptions with an entryId found for this tenant.</div>
                  )}
                </div>
              )}
              <div className="asset-modal-row">
                <label>Serial Number {isAutoSerialChassisType(addForm.type) ? '(auto-generated)' : '*'}</label>
                <input
                  type="text"
                  value={addForm.serial}
                  onChange={(e) => setAddForm(f => ({ ...f, serial: e.target.value }))}
                  disabled={isAutoSerialChassisType(addForm.type)}
                  placeholder={isAutoSerialChassisType(addForm.type) ? 'Auto-generated' : 'Required'}
                />
              </div>
              <div className="asset-modal-row">
                <label>Device Name</label>
                <input
                  type="text"
                  value={addForm.name}
                  onChange={(e) => setAddForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Device name"
                />
              </div>
              <div className="asset-modal-row">
                <label>Model</label>
                <input
                  type="text"
                  value={addForm.model}
                  onChange={(e) => setAddForm(f => ({ ...f, model: e.target.value }))}
                  placeholder="Model"
                />
              </div>
              <div className="asset-modal-row">
                <label>Manufacturer</label>
                <input
                  type="text"
                  value={addForm.manufacturer}
                  onChange={(e) => setAddForm(f => ({ ...f, manufacturer: e.target.value }))}
                  placeholder="Manufacturer"
                />
              </div>
              <div className="asset-modal-row">
                <label>Asset State</label>
                <select
                  value={addForm.asset_state}
                  onChange={(e) => setAddForm(f => ({ ...f, asset_state: e.target.value }))}
                >
                  <option value="">—</option>
                  {ASSET_STATES.map((state) => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div className="asset-modal-row">
                <label>{addForm.type === 'License' ? 'Unit price' : 'Price'}</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={addForm.price}
                  onChange={(e) => setAddForm(f => ({ ...f, price: e.target.value }))}
                  placeholder={addForm.type === 'License' ? 'Per license unit' : 'Optional'}
                />
              </div>
              <div className="asset-modal-row">
                <label>Cost frequency</label>
                <select
                  value={addForm.frequency}
                  onChange={(e) => setAddForm(f => ({ ...f, frequency: e.target.value }))}
                >
                  <option value="">—</option>
                  {ASSET_FREQUENCIES.map((freq) => (
                    <option key={freq} value={freq}>{freq}</option>
                  ))}
                </select>
              </div>
              <div className="asset-modal-row">
                <label>Warranty End</label>
                <input
                  type="date"
                  value={addForm.warrantyEnd}
                  onChange={(e) => setAddForm(f => ({ ...f, warrantyEnd: e.target.value }))}
                />
              </div>
              <div className="asset-modal-row asset-modal-typeahead">
                <label>Assigned To</label>
                <div className="typeahead-wrapper">
                  <input
                    type="text"
                    value={addForm.assigned_to}
                    onChange={(e) => {
                      setAddForm(f => ({ ...f, assigned_to: e.target.value }));
                      setUserSearchQuery(e.target.value);
                    }}
                    onFocus={() => userSearchQuery && setUserSuggestions(userSuggestions)}
                    placeholder="Search by display name..."
                  />
                  {userSuggestions.length > 0 && (
                    <ul className="typeahead-dropdown">
                      {userSuggestions.map((u, i) => {
                        const displayName = u.displayName || u.displayname || u.userPrincipalName || u.userprincipalname || '';
                        return (
                          <li
                            key={i}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setAddForm(f => ({ ...f, assigned_to: displayName }));
                              setUserSearchQuery('');
                              setUserSuggestions([]);
                            }}
                          >
                            {displayName}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
              <div className="asset-modal-actions">
                <button type="button" onClick={closeAddModal} className="asset-modal-cancel">
                  Cancel
                </button>
                <button type="submit" disabled={addSubmitting} className="asset-modal-submit">
                  {addSubmitting ? 'Adding...' : 'Add Asset'}
                </button>
              </div>
            </form>
            ) : (
            <form onSubmit={handleImportFromJson} className="asset-import-form">
              {addError && <div className="asset-modal-error">{addError}</div>}
              <div className="asset-modal-row">
                <label>JSON (paste or upload file)</label>
                <textarea
                  value={jsonInput}
                  onChange={(e) => setJsonInput(e.target.value)}
                  placeholder='{"Peripheral":[{"MachineType":"...","SystemManufacturer":"...","SerialNumber":"...","ChassisName":"Monitor",...}]}'
                  rows={10}
                  className="asset-import-textarea"
                />
                <div className="asset-import-file-row">
                  <input
                    type="file"
                    accept=".json,application/json"
                    onChange={handleJsonFileSelect}
                    className="asset-import-file-input"
                  />
                  <span className="asset-import-file-hint">Field mapping: SystemManufacturer → Manufacturer, ChassisName → Chassis Type, MachineType → Model, SerialNumber → Serial, Discovered → Last Connected, UPN (root) → upn</span>
                </div>
              </div>
              {importResults && (
                <div className="asset-import-results">
                  <div className="asset-import-success">
                    Added: {importResults.success.length} ({importResults.success.map(s => s.serial).join(', ')})
                  </div>
                  {importResults.failed.length > 0 && (
                    <div className="asset-import-failed">
                      Failed: {importResults.failed.length}
                      <ul>
                        {importResults.failed.map((f, i) => (
                          <li key={i}>{f.item?.SerialNumber || f.item?.MachineType || '?'}: {f.error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              <div className="asset-modal-actions">
                <button type="button" onClick={closeAddModal} className="asset-modal-cancel">
                  Cancel
                </button>
                <button type="submit" disabled={addSubmitting} className="asset-modal-submit">
                  {addSubmitting ? 'Importing...' : 'Import devices'}
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      )}

      {noteModalAsset && (
        <div className="asset-modal-overlay" onClick={closeNoteModal}>
          <div className="asset-modal asset-note-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Asset note</h3>
            <p className="asset-note-modal-meta">
              {noteModalAsset.serial || noteModalAsset.name || `Asset #${noteModalAsset.id}`}
            </p>
            {noteError && <div className="asset-modal-error">{noteError}</div>}
            <div className="asset-modal-row">
              <label>Note</label>
              <textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="Add a note for this asset..."
                autoFocus
              />
            </div>
            <div className="asset-modal-actions">
              <button type="button" onClick={closeNoteModal} className="asset-modal-cancel" disabled={savingNote}>
                Cancel
              </button>
              <button type="button" onClick={saveAssetNote} className="asset-modal-submit" disabled={savingNote}>
                {savingNote ? 'Saving...' : 'Save note'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Assets;

